import * as React from "react";
import useArticles from "../hooks/useArticles";
import { ArticleStatListItem } from "../types";
import FilterModal from "./FilterModal";
import useTopics from "../hooks/useTopics";
import { useTags } from "../hooks/useTags";
import useAdminUsers from "../hooks/useAdminUsers";
import LoadingSpinner from "@/components/LoadingSpinner";
import {
  PrimaryBlueButton,
  SecondaryBlueButton
} from "@velaro/velaro-component-lib";
import useToastAlert from "@/hooks/useToastAlert";
import { Severity } from "@velaro/velaro-component-lib";

function getHelpfulPercentage(article: ArticleStatListItem) {
  if (!article.VoteCount || article.VoteCount === 0) {
    return "0%";
  }
  return `${Math.round(((article.HelpfulCount ?? 0) / article.VoteCount) * 100)}%`;
}

function formatDate(isoDate: string | null | undefined) {
  if (!isoDate) return "N/A";
  try {
    const date = new Date(isoDate);
    if (isNaN(date.getTime())) return "N/A";
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  } catch {
    return "N/A";
  }
}

function getStatusBadge(isPublished: boolean, visibility: string) {
  if (!isPublished) {
    return (
      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
        Draft
      </span>
    );
  }
  if (visibility === "Internal") {
    return (
      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
        Internal
      </span>
    );
  }
  return (
    <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
      Public
    </span>
  );
}

export default function Index() {
  const [searchTerm, setSearchTerm] = React.useState("");
  const [showFilterModal, setShowFilterModal] = React.useState(false);
  const [copiedId, setCopiedId] = React.useState<string | null>(null);
  const { topics } = useTopics();
  const { tags } = useTags();
  const { adminUsers } = useAdminUsers();
  const toastAlert = useToastAlert();

  const { articles, loadingArticles, filterState, setFilterState } =
    useArticles();

  React.useEffect(() => {
    setSearchTerm(filterState.searchTerm);
  }, [filterState]);

  function copyLink(article: ArticleStatListItem) {
    if (!article.Url) {
      toastAlert.displayToast(Severity.Warning, "No public URL available for this article");
      return;
    }
    navigator.clipboard.writeText(article.Url).then(() => {
      setCopiedId(article.Id);
      toastAlert.displayToast(Severity.Success, "Link copied to clipboard");
      setTimeout(() => setCopiedId(null), 2000);
    }).catch(() => {
      toastAlert.displayToast(Severity.Error, "Failed to copy link");
    });
  }

  return (
    <>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-slate-800">
              Knowledge Base Articles
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Manage articles for your customer-facing knowledge base and bot training
            </p>
          </div>
          <div className="flex items-center gap-3">
            <SecondaryBlueButton
              label="Filter"
              onClick={() => setShowFilterModal(true)}
            />
            <PrimaryBlueButton
              label="New Article"
              onClick={() => {
                window.location.href = "/kb/articles/create";
              }}
            />
          </div>
        </div>

        {/* Search Bar */}
        <div className="mb-6">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              setFilterState({ ...filterState, searchTerm });
            }}
            className="flex gap-2"
          >
            <div className="relative flex-1 max-w-md">
              <i className="fa fa-search absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm" />
              <input
                type="text"
                className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Search articles by title, content, or tags..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.currentTarget.value)}
              />
            </div>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Search
            </button>
            {filterState.searchTerm && (
              <button
                type="button"
                className="px-3 py-2 text-sm text-slate-600 hover:text-slate-800"
                onClick={() => {
                  setSearchTerm("");
                  setFilterState({ ...filterState, searchTerm: "" });
                }}
              >
                Clear
              </button>
            )}
          </form>
        </div>

        {/* Articles Table */}
        <div className="bg-white rounded-lg border shadow-sm">
          {/* Table Header */}
          <div className="grid grid-cols-12 gap-4 px-4 py-3 bg-slate-50 border-b text-xs font-medium text-slate-500 uppercase tracking-wider rounded-t-lg">
            <div className="col-span-5">Article</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-1 text-center">Views</div>
            <div className="col-span-1 text-center">Helpful</div>
            <div className="col-span-1 text-center">Updated</div>
            <div className="col-span-2 text-right">Actions</div>
          </div>

          {/* Loading overlay for existing articles */}
          {articles.length > 0 && loadingArticles && (
            <div className="relative">
              <div className="absolute inset-0 bg-white/60 z-10 flex items-center justify-center">
                <LoadingSpinner />
              </div>
            </div>
          )}

          {/* Initial loading */}
          {articles.length === 0 && loadingArticles && (
            <div className="flex justify-center py-16">
              <LoadingSpinner />
            </div>
          )}

          {/* Article Rows */}
          <div className="divide-y max-h-[calc(100vh-320px)] overflow-y-auto">
            {articles.map((article) => (
              <div
                key={article.Id}
                className="grid grid-cols-12 gap-4 px-4 py-3 items-center hover:bg-slate-50 transition-colors"
              >
                {/* Title & Topic */}
                <div className="col-span-5 min-w-0">
                  <a
                    href={`/kb/articles/edit/${article.Id}`}
                    className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline truncate block"
                  >
                    {article.Title || "Untitled"}
                  </a>
                  <div className="text-xs text-slate-400 mt-0.5 truncate">
                    {article.TopicName || "No Topic"}
                    {article.Description && (
                      <span> &middot; {article.Description}</span>
                    )}
                  </div>
                </div>

                {/* Status Badge */}
                <div className="col-span-2 flex items-center gap-1.5">
                  {getStatusBadge(article.IsPublished, article.Visibility)}
                  {article.EnableBotResponse && (
                    <span
                      className="px-1.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700"
                      title={article.BotSummary ? "Bot response ready" : "Bot enabled but no summary"}
                    >
                      <i className="fa fa-robot text-[10px] mr-0.5" />
                      Bot
                    </span>
                  )}
                </div>

                {/* Views */}
                <div className="col-span-1 text-center">
                  <span className="text-sm font-medium text-slate-700">
                    {(article.HitCount ?? 0).toLocaleString()}
                  </span>
                </div>

                {/* Helpful */}
                <div className="col-span-1 text-center">
                  <span className="text-sm text-slate-600">
                    {getHelpfulPercentage(article)}
                  </span>
                </div>

                {/* Updated */}
                <div className="col-span-1 text-center">
                  <span className="text-xs text-slate-500">
                    {formatDate(article.UpdatedAt)}
                  </span>
                </div>

                {/* Actions */}
                <div className="col-span-2 flex justify-end gap-1">
                  {/* Copy Link */}
                  {article.IsPublished && article.Url && (
                    <button
                      className="p-1.5 text-slate-400 hover:text-blue-600 rounded hover:bg-blue-50 transition-colors"
                      onClick={() => copyLink(article)}
                      title="Copy article link"
                      aria-label="Copy article link"
                    >
                      <i
                        className={`fa ${
                          copiedId === article.Id ? "fa-check" : "fa-link"
                        } text-sm`}
                      />
                    </button>
                  )}

                  {/* Open in new tab */}
                  {article.IsPublished &&
                    article.Visibility === "Public" &&
                    article.Url && (
                      <a
                        className="p-1.5 text-slate-400 hover:text-blue-600 rounded hover:bg-blue-50 transition-colors"
                        href={article.Url}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Open article in new tab"
                        aria-label="Open article in new tab"
                      >
                        <i className="fa fa-external-link text-sm" />
                      </a>
                    )}

                  {/* Edit */}
                  <a
                    className="p-1.5 text-slate-400 hover:text-blue-600 rounded hover:bg-blue-50 transition-colors"
                    href={`/kb/articles/edit/${article.Id}`}
                    title="Edit article"
                    aria-label="Edit article"
                  >
                    <i className="fa fa-pencil text-sm" />
                  </a>
                </div>
              </div>
            ))}
          </div>

          {/* Empty State */}
          {!loadingArticles && articles.length === 0 && (
            <div className="text-center py-16 px-4">
              <i className="fa fa-book text-4xl text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-600 mb-2">
                No articles found
              </h3>
              <p className="text-sm text-slate-400 mb-6 max-w-md mx-auto">
                {filterState.searchTerm
                  ? `No articles match "${filterState.searchTerm}". Try a different search term or adjust your filters.`
                  : "Get started by creating your first knowledge base article. Articles help your customers find answers and can be used by bots to respond automatically."}
              </p>
              {!filterState.searchTerm && (
                <PrimaryBlueButton
                  label="Create First Article"
                  onClick={() => {
                    window.location.href = "/kb/articles/create";
                  }}
                />
              )}
            </div>
          )}

          {/* Article Count Footer */}
          {articles.length > 0 && (
            <div className="px-4 py-2 bg-slate-50 border-t text-xs text-slate-500 rounded-b-lg">
              {articles.length} article{articles.length !== 1 ? "s" : ""}
            </div>
          )}
        </div>
      </div>

      {showFilterModal && (
        <FilterModal
          topics={topics}
          tags={tags}
          adminUsers={adminUsers}
          onApply={(filterState) => {
            setFilterState(filterState);
            setShowFilterModal(false);
          }}
          onClose={() => setShowFilterModal(false)}
          filterState={filterState}
        />
      )}
    </>
  );
}
